(ns manage.resource.content-variants
  (:require [clojure.set :as set]
            [clojure.string :as str]
            [common.url-template :as url-tpl]
            [common.util :refer [now preview-token->auth-token]]
            [mate.re-frame :as mr]
            [re-frame.core :as rf]))

(def default-sort
  ["-default-variant" "-updated-at" "+import-index"])

(defn get-column-type
  [name columns]
  (let [index (into {} (map (juxt :name :class)) columns)]
    (get index name)))

(defn column->sort-mapping
  [column type]
  (let [sort-by-type
        (case type
          "identifier-key" "content-id"
          "targeting-key" (str "targeting-values>" column)
          "click-through-key" (str "click-through>" column)
          "content-key" (str "content-values>" column)
          nil)]
    (if sort-by-type
      sort-by-type
      (case column
        "sequence-number" "import-index"
        "updated-at" "updated-at"
        "status" "variant-status>"
        "score" "createlift>score"
        "renders" "render-status>"
        ;; if we cannot recognise type or content of column it used as it
        column))))

(defn get-sort
  [db-sort columns]
  (let [{:keys [column direction]} db-sort
        res (if (and column direction (not= column :name))
              [(str (if (= direction :asc) "+" "-")
                    (column->sort-mapping
                     column
                     (get-column-type column columns)))]
              default-sort)]
    res))

(rf/reg-event-fx :resource.content-variants/fetch-all
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)
   (rf/inject-cofx :sub :table.content-variants/filters)
   (rf/inject-cofx :sub :table.content-variants/sort)
   (rf/inject-cofx :sub :table.content-variants/columns)]
  (fn [{:keys [db url-context] :as cofx} [_ {:keys [size cursor boundary]}]]
    {:db (assoc-in db [:resource.content-variants/all :state] :pending)
     :http
     {:method :post
      :params {:muix-data-grid (:table.content-variants/filters cofx)}
      :uri (url-tpl/render
            :api/content-build-variants
            (assoc url-context :feed-importer-id (:resource.adset/current-feed-id cofx))
            (cond-> {:limit size}
              (and (string? cursor) (keyword? boundary)) (assoc boundary cursor)
              (some nil? [cursor boundary]) (assoc :sort (get-sort (:table.content-variants/sort cofx)
                                                                   (:table.content-variants/columns cofx)))))
      :headers {:Authorization (:resource.user/token cofx)}
      :on-success [:resource.content-variants/fetch-all-success]
      :on-failure [:resource.content-variants/fetch-all-failure]}}))

(defn- maybe-schedule-content-variant-reload [effects]
  (let [db (:db effects)
        already-has-scheduled-reload (:table.content-variants/has-scheduled-reload db)
        reload-after-in-ms (:resource.content-variants/reload-after-in-ms db)]
    (cond-> effects
      (and (not already-has-scheduled-reload)
           (some? reload-after-in-ms))
      (-> (mr/update-db assoc :table.content-variants/has-scheduled-reload true)
          (mr/conj-fx [:dispatch-later {:ms reload-after-in-ms
                                        :dispatch [:table.content-variants/refresh-if-data-is-still-needed-now]}])))))

(rf/reg-event-fx :table.content-variants/refresh-if-data-is-still-needed-now
  (fn [{:keys [db]} _]
    (let [reload-after-in-ms (:resource.content-variants/reload-after-in-ms db)]
      (-> {:db db}
          (mr/update-db dissoc :table.content-variants/has-scheduled-reload)
          ;; The value at :resource.content-variants/reload-after-in-ms is dissoc'ed when we navigate away from the page.
          (mr/conj-fx (when (some? reload-after-in-ms)
                        [:dispatch [:table.content-variants/refresh]]))))))

(rf/reg-event-fx :resource.content-variants/fetch-all-success
  (fn [{:keys [db]} [_ value]]
    (-> {:db db}
        (mr/update-db assoc-in [:resource.content-variants/all :state] :success)
        (mr/update-db assoc-in [:resource.content-variants/all :value] value)
        (maybe-schedule-content-variant-reload))))

(rf/reg-event-fx :resource.content-variants/fetch-all-failure
  (fn [{:keys [db]} _]
    (-> {:db db}
        (mr/update-db assoc :resource.content-variants/all {:state :failure
                                                            :value nil})
        (maybe-schedule-content-variant-reload))))

(rf/reg-event-fx :resource.content-variants/get-adjacent
  [(rf/inject-cofx :sub :url-context)]
  (fn [{:keys [db url-context]} [_ cursor]]
    (let [token (preview-token->auth-token (:preview-token url-context))]
      {:db (update db :resource.content-variants/adjacent assoc :state :pending)
       :fx [[:http {:method :get
                    :uri (url-tpl/render
                          :api/content-build-variants
                          (-> url-context
                              (set/rename-keys {:feed-id :feed-importer-id}))
                          (if (str/blank? cursor)
                            {:limit 2 :sort "+id"}
                            {:limit 2 :starting-at cursor}))
                    :headers {:Authorization token}
                    :on-success [:resource.content-variants/get-next-success]
                    :on-failure [:resource.content-variants/get-next-failure]}]
            (when-not (str/blank? cursor)
              [:http {:method :get
                      :uri (url-tpl/render
                            :api/content-build-variants
                            (-> url-context
                                (set/rename-keys {:feed-id :feed-importer-id}))
                            {:limit 1 :ending-before cursor})
                      :headers {:Authorization token}
                      :on-success [:resource.content-variants/get-previous-success]
                      :on-failure [:resource.content-variants/get-previous-failure]}])]})))

(rf/reg-event-db :resource.content-variants/get-next-success
  (fn [db [_ {[current next] :data}]]
    (-> db
        (assoc-in [:resource.content-variants/adjacent :state] :success)
        (assoc-in [:resource.content-variants/adjacent :next] next)
        (assoc-in [:resource.content-variants/adjacent :current] current))))

(rf/reg-event-db :resource.content-variants/get-next-failure
  (fn [db _]
    (assoc-in db [:resource.content-variants/adjacent :state] :failure)))

(rf/reg-event-db :resource.content-variants/get-previous-success
  (fn [db [_ {[previous] :data}]]
    (assoc-in db [:resource.content-variants/adjacent :previous] previous)))

(rf/reg-event-db :resource.content-variants/get-previous-failure
  (fn [db _]
    db))

(rf/reg-event-fx :resource.content-variants/load-more-variants
  (fn [{:keys [db]}]
    (let [last-cursor (get-in db [:variants-preview.variants :last-cursor])]
      {:dispatch [:resource.content-variants/get-variants-for-preview last-cursor]})))

(rf/reg-event-fx :resource.content-variants/get-variants-for-preview
  [(rf/inject-cofx :sub :url-context)]
  (fn [{:keys [db url-context]} [_ cursor]]
    (let [token (preview-token->auth-token (:preview-token url-context))]
      {:db (dissoc db :resource.content-variants/variants)
       :fx [[:http {:method :get
                    :uri (url-tpl/render
                          :api/content-build-variants
                          (assoc url-context :feed-importer-id (:feed-id url-context))
                          (if (str/blank? cursor)
                            {:limit 100 :sort default-sort}
                            {:limit 100 :starting-at cursor}))
                    :headers {:Authorization token}
                    :on-success [:resource.content-variants/get-variants-for-preview-success]
                    :on-failure [:resource.content-variants/get-variants-for-preview-failure]}]]})))

(rf/reg-event-fx :resource.content-variants/get-variants-for-preview-success
  (fn [{:keys [db]} [_ {variants :data}]]
    {:fx [(when (= (count variants) 100) [:dispatch [:resource.content-variants/load-more-variants]])]
     :db (assoc db :resource.content-variants/variants variants)}))

(rf/reg-event-db :resource.content-variants/get-variants-for-preview-failure
  (fn [db _]
    db))

(rf/reg-sub :resource.content-variants/all
  (fn [db]
    (:resource.content-variants/all db)))

(defn variant-id->idx [id variants]
  (first (keep-indexed (fn [idx v]
                         (when (= (:id (:data v)) id)
                           idx))
                       variants)))

(defn update-variants [variants ids update-fn & xyz]
  (mapv (fn [{{:keys [id]} :data :as variant}]
          (if ((set ids) id)
            (apply update-fn variant xyz)
            variant)) variants))

(rf/reg-event-fx :resource.content-variants/approve-many
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ variant-ids {:keys [feed-id-override success-event]
                                                                :or {success-event [:resource.content-variants/approve-many-success]}}]]
    (let [feed-id (or feed-id-override current-feed-id)]
      {:fx [[:http
             {:method :post
              :uri (url-tpl/render :api/content-build-variant-approve-many
                                   (assoc url-context :feed-importer-id feed-id))
              :headers {:Authorization token}
              :params {:variant-ids variant-ids}
              :on-success success-event
              :on-failure [:resource.content-variants/approve-many-failure]}]]})))

(rf/reg-event-db :resource.content-variants/approve-many-success
  (fn [db [_ variant-ids]]
    (update-in db [:resource.content-variants/all :value :data]
               update-variants variant-ids (fn [variant]
                                             (-> variant
                                                 (assoc-in [:data :approved] true)
                                                 (assoc-in [:data :status] "approved")
                                                 (assoc-in [:data :last-approved-ts] (now)))))))

(rf/reg-event-db :resource.content-variants/approve-many-failure
  (fn [db]
    db))

(rf/reg-event-fx :resource.content-variants/unapprove-many
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ variant-ids {:keys [feed-id-override success-event]
                                                                :or {success-event [:resource.content-variants/unapprove-many-success]}}]]
    (let [feed-id (or feed-id-override current-feed-id)]
      {:fx [[:http
             {:method :post
              :uri (url-tpl/render :api/content-build-variant-unapprove-many
                                   (assoc url-context :feed-importer-id feed-id))
              :headers {:Authorization token}
              :params {:variant-ids variant-ids}
              :on-success success-event
              :on-failure [:resource.content-variants/unapprove-many-failure]}]]})))

(rf/reg-event-db :resource.content-variants/unapprove-many-success
  (fn [db [_ variant-ids]]
    (update-in db [:resource.content-variants/all :value :data]
               update-variants variant-ids (fn [variant]
                                             (-> (assoc-in variant [:data :approved] false)
                                                 (assoc-in [:data :status] "draft")
                                                 (assoc-in [:data :last-published-ts] nil))))))

(rf/reg-event-db :resource.content-variants/unapprove-many-failure
  (fn [db]
    db))

(rf/reg-event-fx :resource.content-variants/publish-many
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ variant-ids]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-publish-many
                                 (assoc url-context :feed-importer-id current-feed-id))
            :headers {:Authorization token}
            :params {:variant-ids variant-ids}
            :on-success [:resource.content-variants/publish-many-success]
            :on-failure [:resource.content-variants/publish-many-failure]}]]}))

(rf/reg-event-db :resource.content-variants/publish-many-success
  (fn [db [_ variant-ids]]
    (update-in db [:resource.content-variants/all :value :data]
               update-variants variant-ids (fn [variant]
                                             (-> (assoc-in variant [:data :published] true)
                                                 (assoc-in [:data :status] "publishing")
                                                 (assoc-in [:data :last-published-ts] (now)))))))

(rf/reg-event-db :resource.content-variants/publish-many-failure
  (fn [db]
    db))

(rf/reg-event-fx :resource.content-variants/unpublish-many
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ variant-ids]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-unpublish-many
                                 (assoc url-context :feed-importer-id current-feed-id))
            :headers {:Authorization token}
            :params {:variant-ids variant-ids}
            :on-success [:resource.content-variants/unpublish-many-success]
            :on-failure [:resource.content-variants/unpublish-many-failure]}]]}))

(rf/reg-event-db :resource.content-variants/unpublish-many-success
  (fn [db [_ variant-ids]]
    (update-in db [:resource.content-variants/all :value :data]
               update-variants variant-ids (fn [variant]
                                             (-> variant
                                                 (assoc-in [:data :status] "unpublishing")
                                                 (assoc-in [:data :published] false))))))

(rf/reg-event-db :resource.content-variants/unpublish-many-failure
  (fn [db]
    db))

(rf/reg-event-fx :resource.content-variants/approve-then-publish-many
  (fn [_ [_ variant-ids]]
    {:async-flow
     {:id :resource.content-variants/approve-then-publish-flow
      :first-dispatch [:resource.content-variants/approve-many variant-ids]
      :rules [{:when :seen?
               :halt? true
               :events :resource.content-variants/approve-many-success
               :dispatch [:resource.content-variants/publish-many variant-ids]}]}}))

(rf/reg-event-fx :resource.content-variants/unpublish-then-unapprove-many
  (fn [_ [_ variant-ids]]
    {:async-flow
     {:id :resource.content-variants/unpublish-then-unapprove-flow
      :first-dispatch [:resource.content-variants/unpublish-many variant-ids]
      :rules [{:when :seen?
               :halt? true
               :events :resource.content-variants/unpublish-many-success
               :dispatch [:resource.content-variants/unapprove-many variant-ids]}]}}))

(rf/reg-event-fx :resource.content-variants/trigger-render-many
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ variant-ids action]]
    (if (= :trigger action)
      {:fx [[:http
             {:method :post
              :uri (url-tpl/render :api/content-build-feed-trigger-render
                                   (assoc url-context :feed-importer-id current-feed-id))
              :headers {:Authorization token}
              :params {:variant-ids variant-ids}
              :on-success [:resource.content-variants/trigger-many-success]
              :on-failure [:resource.content-variants/trigger-many-failure]}]]}
      {:fx [[:http
             {:method :post
              :uri (url-tpl/render :api/content-build-feed-cancel-render
                                   (assoc url-context :feed-importer-id current-feed-id))
              :headers {:Authorization token}
              :params {:variant-ids variant-ids}
              :on-success [:resource.content-variants/trigger-many-success]
              :on-failure [:resource.content-variants/trigger-many-failure]}]]})))

(rf/reg-event-db :resource.content-variants/trigger-many-success
  (fn [db [_]]
    db))

(rf/reg-event-db :resource.content-variants/trigger-many-failure
  (fn [db]
    db))

(rf/reg-event-fx :resource.content-variants/approve-by-feed
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ filters]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-approve-by-feed
                                 (assoc url-context :feed-importer-id current-feed-id))
            :headers {:Authorization token}
            :params {:muix-data-grid filters}
            :on-success [:resource.content-variants/approve-many-success]
            :on-failure [:resource.content-variants/approve-many-failure]}]]}))

(rf/reg-event-fx :resource.content-variants/unapprove-by-feed
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ filters]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-unapprove-by-feed
                                 (assoc url-context :feed-importer-id current-feed-id))
            :headers {:Authorization token}
            :params {:muix-data-grid filters}
            :on-success [:resource.content-variants/unapprove-many-success]
            :on-failure [:resource.content-variants/unapprove-many-failure]}]]}))

(rf/reg-event-fx :resource.content-variants/publish-by-feed
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ filters]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-publish-by-feed
                                 (assoc url-context :feed-importer-id current-feed-id))
            :headers {:Authorization token}
            :params {:muix-data-grid filters}
            :on-success [:resource.content-variants/publish-many-success]
            :on-failure [:resource.content-variants/publish-many-failure]}]]}))

(rf/reg-event-fx :resource.content-variants/unpublish-by-feed
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ filters]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-unpublish-by-feed
                                 (assoc url-context :feed-importer-id current-feed-id))
            :headers {:Authorization token}
            :params {:muix-data-grid filters}
            :on-success [:resource.content-variants/unpublish-many-success]
            :on-failure [:resource.content-variants/unpublish-many-failure]}]]}))

(rf/reg-event-fx :resource.content-variants/get-defaults
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ _]]
    {:fx [[:http
           {:method :get
            :uri (url-tpl/render :api/content-build-variant-defaults
                                 (assoc url-context :feed-importer-id current-feed-id))
            :headers {:Authorization token}
            :on-success [:resource.content-variants/get-defaults-success]
            :on-failure [:resource.content-variants/get-defaults-failure]}]]}))

(rf/reg-event-db :resource.content-variants/get-defaults-success
  (fn [db [_ default-variants]]
    (assoc db :resource.content-variants/defaults default-variants)))

(rf/reg-event-db :resource.content-variants/get-defaults-failure
  (fn [db]
    (dissoc db :resource.content-variants/defaults)))

(rf/reg-sub
  :resource.content-variants/defaults
  (fn [db _]
    (:resource.content-variants/defaults db)))

(rf/reg-event-fx :resource.content-variants/set-default
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ [variant-id]]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-set-default
                                 (assoc url-context :feed-importer-id current-feed-id :variant-id variant-id))
            :headers {:Authorization token}
            :on-success [:resource.content-variants/set-default-success variant-id]
            :on-failure [:resource.content-variants/set-default-failure]}]]}))

(rf/reg-event-fx :resource.content-variants/set-default-success
  (fn [_ _]
    {:fx [[:dispatch [:table.content-variants/refresh]]]}))

(rf/reg-event-fx :resource.content-variants/set-default-failure
  (fn [_cofx [event response]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name "Content variant"
                       :type :error
                       :message (get-in response [:value :message])
                       :event event}]]]}))

(rf/reg-event-fx :resource.content-variants/get-variant-renders
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ variant-id]]
    {:fx [[:http
           {:method :get
            :uri (url-tpl/render :api/content-build-variant-renders
                                 (assoc url-context :feed-importer-id current-feed-id :variant-id variant-id))
            :headers {:Authorization token}
            :on-success [:resource.content-variants/get-variant-renders-success variant-id]
            :on-failure [:resource.content-variants/get-variant-renders-failure variant-id]}]]}))

(rf/reg-event-db :resource.content-variants/get-variant-renders-success
  (fn [db [_ variant-id {:keys [data]}]]
    (let [variants (get-in db [:resource.content-variants/all :value :data])
          idx (variant-id->idx variant-id variants)]
      (assoc-in db [:resource.content-variants/all :value :data idx :data :renders] data))))

(rf/reg-event-db :resource.content-variants/get-variant-renders-failure
  (fn [db [_ variant-id]]
    (let [variants (get-in db [:resource.content-variants/all :value :data])
          idx (variant-id->idx variant-id variants)]
      (assoc-in db [:resource.content-variants/all :value :data idx :data :renders] nil))))

(rf/reg-event-fx :resource.content-variants/get-unique-values
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context] :as cofx} [_ column-name]]
    {:fx [[:http
           {:method :get
            :uri (url-tpl/render
                  :api/content-build-unique-values
                  (assoc url-context :feed-importer-id (:resource.adset/current-feed-id cofx) :column-name column-name))
            :headers {:Authorization (:resource.user/token cofx)}
            :on-success [:resource.content-variants/get-unique-values-success column-name]
            :on-failure [:resource.content-variants/get-unique-values-failure]}]]}))

(rf/reg-event-db :resource.content-variants/get-unique-values-success
  (fn [db [_ column-name {:keys [data]}]]
    (assoc-in db [:resource.content-variants/unique-values column-name] data)))

(rf/reg-event-db :resource.content-variants/get-unique-values-failure
  (fn [db _]
    (assoc db :resource.content-variants/unique-values nil)))

(rf/reg-sub :resource.content-variants/unique-values
  (fn [db [_ column-name]]
    (get-in db [:resource.content-variants/unique-values column-name])))

(rf/reg-event-fx :resource.content-variants/get-states
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.adset/current-feed-id)]
  (fn [{:keys [url-context]
        :resource.user/keys [token]
        :resource.adset/keys [current-feed-id]} [_ filters]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/content-build-variant-states
                                 (assoc url-context :feed-importer-id current-feed-id))
            :params {:muix-data-grid filters}
            :headers {:Authorization token}
            :on-success [:resource.content-variants/get-states-success]
            :on-failure [:resource.content-variants/get-states-failure]}]]}))

(rf/reg-event-db :resource.content-variants/get-states-success
  (fn [db [_ states]]
    (assoc db :resource.content-variants/states states)))

(rf/reg-event-db :resource.content-variants/get-states-failure
  (fn [db]
    (dissoc db :resource.content-variants/states)))

(rf/reg-sub
  :resource.content-variants/states
  (fn [db _]
    (:resource.content-variants/states db)))
